import { useCallback, useEffect, useState } from 'react';
import { fetchAuthWrapper } from '@starjack/api';
import { urlBuilder } from '@starjack/utils';
import { useNotification } from '@starjack/features/notifications';
import {
    useGetVoucherFontsQuery,
    useDeleteVoucherFontMutation,
    voucherTemplateApi,
    VoucherFontDTO,
} from '@starjack/entities/voucherTemplate';
import { useDispatch } from 'react-redux';

const fontsUrl = urlBuilder('order', '/admin/voucher-fonts');

/**
 * Sanitize font name for safe use in CSS @font-face declarations.
 */
const sanitizeFontName = (name: string): string =>
    name.replace(/['"\\<>{}();]/g, '');

/**
 * Fetches font file with auth and injects @font-face rule using a blob URL.
 * This is necessary because CSS url() cannot send JWT auth headers.
 */
const injectFontFace = async (font: VoucherFontDTO) => {
    const styleId = `vt-font-${font.id}`;
    if (document.getElementById(styleId)) return;

    try {
        const fontUrl = `${fontsUrl}/${font.id}/file`;
        const response = await fetchAuthWrapper(fontUrl);
        if (!response.ok) return;

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const safeName = sanitizeFontName(font.name);

        const style = document.createElement('style');
        style.id = styleId;
        style.dataset.blobUrl = blobUrl;
        style.textContent = `
            @font-face {
                font-family: '${safeName}';
                src: url('${blobUrl}') format('${font.fileExtension === 'otf' ? 'opentype' : 'truetype'}');
                font-display: swap;
            }
        `;
        document.head.appendChild(style);
    } catch {
        // Font loading failed silently -- element will use fallback font
    }
};

export const useVoucherFonts = (liftOperatorId?: string) => {
    const { addNotification } = useNotification();
    const dispatch = useDispatch();
    const [isUploading, setIsUploading] = useState(false);

    const { data: fonts = [], isLoading } = useGetVoucherFontsQuery(
        liftOperatorId ? { liftOperatorId } : undefined
    );
    const [deleteFont] = useDeleteVoucherFontMutation();

    // Inject @font-face for all custom fonts
    useEffect(() => {
        for (const font of fonts) {
            if (font.liftOperatorId) {
                injectFontFace(font);
            }
        }
    }, [fonts]);

    const uploadFont = useCallback(
        async (file: File): Promise<boolean> => {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('file', file);
                // Backend requires a `name` part; the operator scope is derived
                // from the auth context server-side, not from the body.
                formData.append('name', file.name.replace(/\.[^./\\]+$/, ''));

                const response = await fetchAuthWrapper(fontsUrl, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error('Font upload failed');
                }

                addNotification({
                    type: 'success',
                    message: `Schriftart "${file.name}" hochgeladen.`,
                });

                // Invalidate RTK Query cache to refresh font list
                dispatch(
                    voucherTemplateApi.util.invalidateTags(['VOUCHER_FONTS'])
                );

                return true;
            } catch {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Hochladen. Nur .otf und .ttf Dateien sind erlaubt.',
                });
                return false;
            } finally {
                setIsUploading(false);
            }
        },
        [liftOperatorId, addNotification, dispatch]
    );

    const handleDeleteFont = useCallback(
        async (font: VoucherFontDTO): Promise<boolean> => {
            try {
                await deleteFont(font.id).unwrap();
                addNotification({
                    type: 'success',
                    message: `Schriftart "${font.name}" gelöscht.`,
                });

                // Remove injected @font-face and revoke blob URL
                const styleEl = document.getElementById(`vt-font-${font.id}`);
                if (styleEl) {
                    const blobUrl = (styleEl as HTMLElement).dataset.blobUrl;
                    if (blobUrl) URL.revokeObjectURL(blobUrl);
                    styleEl.remove();
                }

                return true;
            } catch {
                addNotification({
                    type: 'error',
                    message:
                        'Schriftart wird von einer Vorlage verwendet und kann nicht gelöscht werden.',
                });
                return false;
            }
        },
        [deleteFont, addNotification]
    );

    return {
        fonts,
        isLoading,
        isUploading,
        uploadFont,
        deleteFont: handleDeleteFont,
    };
};
